import { Fragment, useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { cn } from "@/lib/utils";

import {
  TableWrapper,
  TableBody,
  TableRow,
  TableRowItem,
  TableFooter,
  TablePagination,
} from "@/components/ui/table";
import { Avatar } from "@fluentui/react-components";
import { Form } from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { LoaderCircle } from "lucide-react";

import { AdminJobChecklist } from "@/queries/admin/job-checklist";
import {
  JobChecklistFormSchema,
  jobChecklistFormSchema,
  useSetJobChecklist,
} from "@/mutations/admin/set-job-checklist";

import {
  checklistContractStatusesList,
  checklistSourcingStatusesList,
} from "@/types";

const ENTRIES_PER_PAGE = 10;
const SOURCING_STATUS_OPTIONS = checklistSourcingStatusesList;
const SHORTLIST_STATUS_OPTIONS = checklistSourcingStatusesList.filter(
  (v) => v !== "N/A",
);
const MSA_STATUS_OPTIONS = checklistContractStatusesList.filter(
  (v) => v !== "N/A",
);
const SOW_STATUS_OPTIONS = MSA_STATUS_OPTIONS;
const NDA_STATUS_OPTIONS = checklistContractStatusesList;
const STATUS_TO_COLOR_MAP: Record<string, string> = {
  Done: "bg-green-800",
  Signed: "bg-green-800",
  "In progress": "bg-blue-800",
  "Not started": "bg-gray-600",
  "N/A": "bg-gray-200",
};

const OpenSvg = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="15"
    height="10"
    viewBox="0 0 15 10"
    fill="none"
  >
    <path
      d="M1.45971 0.882812H14.0232C14.8923 0.882812 15.3269 1.93262 14.7117 2.54785L8.43236 8.83203C8.0515 9.21289 7.43139 9.21289 7.05053 8.83203L0.771231 2.54785C0.155996 1.93262 0.590567 0.882812 1.45971 0.882812Z"
      fill="#696969"
    />
  </svg>
);

const ClosedSvg = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="9"
    height="16"
    viewBox="0 0 9 16"
    fill="none"
  >
    <path
      d="M0.765137 14.2815L0.765136 1.71803C0.765136 0.848886 1.81494 0.414315 2.43018 1.02955L8.71435 7.30885C9.09521 7.68971 9.09521 8.30982 8.71435 8.69068L2.43018 14.97C1.81494 15.5852 0.765137 15.1506 0.765137 14.2815Z"
      fill="#696969"
    />
  </svg>
);

interface CompanyTableEntry {
  id: string;
  name?: string;
  logoLink?: string;
  jobs: AdminJobChecklist[];
}

const CompanyTable = ({ companies }: { companies?: CompanyTableEntry[] }) => {
  const [page, setPage] = useState(1);
  const [openCompanies, setOpenCompanies] = useState<string[]>([]);

  const numberOfPages = useMemo(() => {
    if (!companies) {
      return 1;
    }

    return Math.ceil(companies.length / ENTRIES_PER_PAGE);
  }, [companies]);

  const currentPageEntries = useMemo(
    () =>
      companies?.slice((page - 1) * ENTRIES_PER_PAGE, page * ENTRIES_PER_PAGE),
    [companies, page],
  );

  useEffect(() => {
    if (page > numberOfPages && page !== 1) {
      setPage(numberOfPages || 1);
    }
  }, [page, numberOfPages]);

  const toggleCompany = (companyId: string) => {
    setOpenCompanies((prev) =>
      prev.includes(companyId)
        ? prev.filter((id) => id !== companyId)
        : [...prev, companyId],
    );
  };

  return (
    <div className="py-6 self-stretch flex flex-col lg:flex-row gap-x-6 gap-y-2">
      <div className="flex-1 min-w-0">
        <TableWrapper>
          {currentPageEntries?.map((company) => (
            <Fragment key={`company-${company.id}`}>
              <div className="p-2 flex flex-row items-center justify-between self-stretch gap-1 shadow-[0px_1px_2px_0px_rgba(82,88,102,0.06)]">
                <div className="flex flex-row items-center gap-10">
                  <div className="flex flex-row gap-1 items-center">
                    <Avatar
                      image={{ src: company.logoLink }}
                      name={company.name}
                      size={40}
                    />
                    <span className="text-sm font-semibold text-[#161618]">
                      {company.name}
                    </span>
                  </div>
                  <p className="text-xs font-normal text-[#5C6060]">
                    {company.jobs.length === 1
                      ? "1 Opportunity"
                      : `${company.jobs.length} Opportunities`}
                  </p>
                </div>
                <div
                  className="p-2 flex flex-column items-center justify-center cursor-pointer"
                  onClick={() => toggleCompany(company.id)}
                >
                  {openCompanies.includes(company.id) ? (
                    <OpenSvg />
                  ) : (
                    <ClosedSvg />
                  )}
                </div>
              </div>
              {openCompanies.includes(company.id) && (
                <JobTable company={company} />
              )}
            </Fragment>
          ))}
          <TableFooter>
            <TablePagination
              currentPage={page}
              totalPages={numberOfPages}
              navigateFn={setPage}
            />
          </TableFooter>
        </TableWrapper>
      </div>
    </div>
  );
};

const JobTable = ({ company }: { company: CompanyTableEntry }) => {
  return (
    <TableBody>
      <thead>
        <TableRow>
          <TableRowItem heading>Job Title</TableRowItem>
          <TableRowItem heading>LOI</TableRowItem>
          <TableRowItem heading>Job Description Approval</TableRowItem>
          <TableRowItem heading className="min-w-40">
            Salary & Scope
          </TableRowItem>
          <TableRowItem heading>MAP Assessments</TableRowItem>
          <TableRowItem heading>Company Profile Approved</TableRowItem>
          <TableRowItem heading>Interview Structure</TableRowItem>
          <TableRowItem heading className="min-w-48">
            Sourcing
          </TableRowItem>
          <TableRowItem heading className="min-w-48">
            Shortlist
          </TableRowItem>
          <TableRowItem heading>Interviewing</TableRowItem>
          <TableRowItem heading className="min-w-48">
            Master Service Agreement
          </TableRowItem>
          <TableRowItem heading className="min-w-48">
            Statement of Work
          </TableRowItem>
          <TableRowItem heading className="min-w-48">
            NDA
          </TableRowItem>
          <TableRowItem heading button></TableRowItem>
        </TableRow>
      </thead>
      <tbody>
        {company.jobs.map((job) => (
          <AdminRow key={`job-${job.id}`} job={job} />
        ))}
      </tbody>
    </TableBody>
  );
};

const AdminRow = ({ job }: { job: AdminJobChecklist }) => {
  const { setJobChecklist, isSettingJobChecklist } = useSetJobChecklist(job.id);

  const form = useForm<JobChecklistFormSchema>({
    resolver: zodResolver(jobChecklistFormSchema),
    defaultValues: {
      loiComplete: job.loiComplete,
      jobDescriptionComplete: job.jobDescriptionComplete,
      cultureComplete: job.cultureComplete,
      companyProfileComplete: job.companyProfileComplete,
      interviewStructureComplete: job.interviewStructureComplete,
      interviewingComplete: job.interviewingComplete,
      salaryAndScope: job.salaryAndScope ?? "",
      sourcingStatus: job.sourcingStatus,
      shortlistStatus: job.shortlistStatus,
      masterServiceAgreementStatus: job.masterServiceAgreementStatus,
      statementOfWorkStatus: job.statementOfWorkStatus,
      ndaStatus: job.ndaStatus,
    },
  });

  return (
    <Form {...form}>
      <TableRow>
        <TableRowItem>
          <span className="text-sm font-semibold text-[#161618]">
            {/* eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing */}
            {job.role || "No Job Title"}
          </span>
        </TableRowItem>
        <TableRowItem>
          <Controller
            name="loiComplete"
            control={form.control}
            render={({ field }) => (
              <Checkbox
                checked={field.value}
                onCheckedChange={(state) => {
                  field.onChange(typeof state === "string" ? false : state);
                }}
              />
            )}
          />
        </TableRowItem>
        <TableRowItem>
          <Controller
            name="jobDescriptionComplete"
            control={form.control}
            render={({ field }) => (
              <Checkbox
                checked={field.value}
                onCheckedChange={(state) => {
                  field.onChange(typeof state === "string" ? false : state);
                }}
              />
            )}
          />
        </TableRowItem>
        <TableRowItem>
          <Controller
            control={form.control}
            name="salaryAndScope"
            render={({ field }) => (
              <Textarea
                {...field}
                placeholder="$10,000/month, 6 months"
                className="min-h-12"
              />
            )}
          />
        </TableRowItem>
        <TableRowItem>
          <Controller
            name="cultureComplete"
            control={form.control}
            render={({ field }) => (
              <Checkbox
                checked={field.value}
                onCheckedChange={(state) => {
                  field.onChange(typeof state === "string" ? false : state);
                }}
              />
            )}
          />
        </TableRowItem>
        <TableRowItem>
          <Controller
            name="companyProfileComplete"
            control={form.control}
            render={({ field }) => (
              <Checkbox
                checked={field.value}
                onCheckedChange={(state) => {
                  field.onChange(typeof state === "string" ? false : state);
                }}
              />
            )}
          />
        </TableRowItem>
        <TableRowItem>
          <Controller
            name="interviewStructureComplete"
            control={form.control}
            render={({ field }) => (
              <Checkbox
                checked={field.value}
                onCheckedChange={(state) => {
                  field.onChange(typeof state === "string" ? false : state);
                }}
              />
            )}
          />
        </TableRowItem>
        <TableRowItem>
          <Controller
            name="sourcingStatus"
            control={form.control}
            render={({ field }) => (
              <Select
                name={field.name}
                value={field.value}
                onValueChange={field.onChange}
                disabled={field.disabled}
              >
                <SelectTrigger>
                  <SelectValue placeholder={"Select one"} />
                </SelectTrigger>
                <SelectContent>
                  {SOURCING_STATUS_OPTIONS.map((status) => (
                    <SelectItem
                      key={`job-${job.id}-sourcing-${status}`}
                      value={status}
                    >
                      <div className="flex flex-row items-center gap-1">
                        <div
                          className={cn(
                            "min-h-2 min-w-2 h-2 w-2 rounded-full",
                            STATUS_TO_COLOR_MAP[status],
                          )}
                        />
                        {status}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </TableRowItem>
        <TableRowItem>
          <Controller
            name="shortlistStatus"
            control={form.control}
            render={({ field }) => (
              <Select
                name={field.name}
                value={field.value}
                onValueChange={field.onChange}
                disabled={field.disabled}
              >
                <SelectTrigger>
                  <SelectValue placeholder={"Select one"} />
                </SelectTrigger>
                <SelectContent>
                  {SHORTLIST_STATUS_OPTIONS.map((status) => (
                    <SelectItem
                      key={`job-${job.id}-shortlist-${status}`}
                      value={status}
                    >
                      <div className="flex flex-row items-center gap-1">
                        <div
                          className={cn(
                            "min-h-2 min-w-2 h-2 w-2 rounded-full",
                            STATUS_TO_COLOR_MAP[status],
                          )}
                        />
                        {status}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </TableRowItem>
        <TableRowItem>
          <Controller
            name="interviewingComplete"
            control={form.control}
            render={({ field }) => (
              <Checkbox
                checked={field.value}
                onCheckedChange={(state) => {
                  field.onChange(typeof state === "string" ? false : state);
                }}
              />
            )}
          />
        </TableRowItem>
        <TableRowItem>
          <Controller
            name="masterServiceAgreementStatus"
            control={form.control}
            render={({ field }) => (
              <Select
                name={field.name}
                value={field.value}
                onValueChange={field.onChange}
                disabled={field.disabled}
              >
                <SelectTrigger>
                  <SelectValue placeholder={"Select one"} />
                </SelectTrigger>
                <SelectContent>
                  {MSA_STATUS_OPTIONS.map((status) => (
                    <SelectItem
                      key={`job-${job.id}-msa-${status}`}
                      value={status}
                    >
                      <div className="flex flex-row items-center gap-1">
                        <div
                          className={cn(
                            "min-h-2 min-w-2 h-2 w-2 rounded-full",
                            STATUS_TO_COLOR_MAP[status],
                          )}
                        />
                        {status}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </TableRowItem>
        <TableRowItem>
          <Controller
            name="statementOfWorkStatus"
            control={form.control}
            render={({ field }) => (
              <Select
                name={field.name}
                value={field.value}
                onValueChange={field.onChange}
                disabled={field.disabled}
              >
                <SelectTrigger>
                  <SelectValue placeholder={"Select one"} />
                </SelectTrigger>
                <SelectContent>
                  {SOW_STATUS_OPTIONS.map((status) => (
                    <SelectItem
                      key={`job-${job.id}-sow-${status}`}
                      value={status}
                    >
                      <div className="flex flex-row items-center gap-1">
                        <div
                          className={cn(
                            "min-h-2 min-w-2 h-2 w-2 rounded-full",
                            STATUS_TO_COLOR_MAP[status],
                          )}
                        />
                        {status}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </TableRowItem>
        <TableRowItem>
          <Controller
            name="ndaStatus"
            control={form.control}
            render={({ field }) => (
              <Select
                name={field.name}
                value={field.value}
                onValueChange={field.onChange}
                disabled={field.disabled}
              >
                <SelectTrigger>
                  <SelectValue placeholder={"Select one"} />
                </SelectTrigger>
                <SelectContent>
                  {NDA_STATUS_OPTIONS.map((status) => (
                    <SelectItem
                      key={`job-${job.id}-nda-${status}`}
                      value={status}
                    >
                      <div className="flex flex-row items-center gap-1">
                        <div
                          className={cn(
                            "min-h-2 min-w-2 h-2 w-2 rounded-full",
                            STATUS_TO_COLOR_MAP[status],
                          )}
                        />
                        {status}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </TableRowItem>
        <TableRowItem>
          <Button
            variant="secondary"
            type="submit"
            className="mt-2"
            disabled={isSettingJobChecklist}
            onClick={(ev) => {
              ev.preventDefault();
              ev.stopPropagation();
              void form.handleSubmit((data) => setJobChecklist(data))();
            }}
          >
            {isSettingJobChecklist && (
              <LoaderCircle className="size-6 text-white animate-spin" />
            )}
            Save
          </Button>
        </TableRowItem>
      </TableRow>
    </Form>
  );
};

export default CompanyTable;
